import { useFormContext, useWatch } from 'react-hook-form';
import { P, Section, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useMemo } from 'react';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { ItemDetailsFormData } from './types';
import { useItemDetailsForm } from './useItemDetailForm';

export function FitDetailsSection() {
  const { control, setValue } = useFormContext<ItemDetailsFormData>();
  const { isFieldDisabled, isFieldRequired, makeOptionsFromMetaData } =
    useItemDetailsForm();

  const [
    currentNeckline,
    currentSleeveLength,
    currentTopLength,
    currentBodyFitTop,
    currentBodyFitBottom,
    currentBottomLength,
  ] = useWatch({
    control,
    name: [
      'neckline',
      'sleeveLength',
      'topLength',
      'bodyFitTop',
      'bodyFitBottom',
      'bottomLength',
    ],
  });

  const necklineOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_neckline', 'Neckline'),
    [makeOptionsFromMetaData],
  );

  const sleeveLengthOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_sleeveLength', 'Sleeve Length'),
    [makeOptionsFromMetaData],
  );

  const topLengthOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_length', 'Item Length'),
    [makeOptionsFromMetaData],
  );

  const bodyFitTopOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_fits', 'Body Fit1 (Top)'),
    [makeOptionsFromMetaData],
  );

  const bodyFitBottomOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_fits2', 'Body Fit1 (Bottom)'),
    [makeOptionsFromMetaData],
  );

  const bottomLengthOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_length', 'Bottom Length'),
    [makeOptionsFromMetaData],
  );

  useEffect(() => {
    if (
      currentNeckline &&
      necklineOptions.length > 0 &&
      !necklineOptions.some((opt) => opt.value === currentNeckline)
    ) {
      setValue('neckline', '');
    }
  }, [necklineOptions]);

  useEffect(() => {
    if (
      currentSleeveLength &&
      sleeveLengthOptions.length > 0 &&
      !sleeveLengthOptions.some((opt) => opt.value === currentSleeveLength)
    ) {
      setValue('sleeveLength', '');
    }
  }, [sleeveLengthOptions]);

  useEffect(() => {
    if (
      currentTopLength &&
      topLengthOptions.length > 0 &&
      !topLengthOptions.some((opt) => opt.value === currentTopLength)
    ) {
      setValue('topLength', '');
    }
  }, [topLengthOptions]);

  useEffect(() => {
    if (
      currentBodyFitTop &&
      bodyFitTopOptions.length > 0 &&
      !bodyFitTopOptions.some((opt) => opt.value === currentBodyFitTop)
    ) {
      setValue('bodyFitTop', '');
    }
  }, [bodyFitTopOptions]);

  useEffect(() => {
    if (
      currentBodyFitBottom &&
      bodyFitBottomOptions.length > 0 &&
      !bodyFitBottomOptions.some((opt) => opt.value === currentBodyFitBottom)
    ) {
      setValue('bodyFitBottom', '');
    }
  }, [bodyFitBottomOptions]);

  useEffect(() => {
    if (
      currentBottomLength &&
      bottomLengthOptions.length > 0 &&
      !bottomLengthOptions.some((opt) => opt.value === currentBottomLength)
    ) {
      setValue('bottomLength', '');
    }
  }, [bottomLengthOptions]);

  return (
    <Section
      icon="pencil-and-scale/scale-pencil-cross"
      title="Fit Details"
      color="indigo"
      className="w-full flex flex-col"
    >
      <Stack direction="col" gap="md">
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="length"
            label="Length"
            control={control}
            inputType="number"
            inputProps={{
              placeholder: '0',
              className: 'w-full',
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  inches
                </P>
              ),
            }}
          />
          <ControlledFormInput
            name="inseam"
            label="Inseam"
            control={control}
            inputType="number"
            inputProps={{
              placeholder: '0',
              className: 'w-full',
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  inches
                </P>
              ),
            }}
          />
          <ControlledFormInput
            name="stretch"
            label="Stretch"
            control={control}
            inputType="multiselect"
            disabled
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: [],
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="neckline"
            label="Neckline"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('neckline', false) || necklineOptions.length <= 0
            }
            required={isFieldRequired('neckline', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: necklineOptions,
            }}
          />
          <ControlledFormInput
            name="sleeveLength"
            label="Sleeve Length"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('sleeve_length', false) ||
              sleeveLengthOptions.length <= 0
            }
            required={isFieldRequired('sleeve_length', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: sleeveLengthOptions,
            }}
          />
          <ControlledFormInput
            name="topLength"
            label="Top Length"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('top_length', false) ||
              topLengthOptions.length <= 0
            }
            required={isFieldRequired('top_length', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: topLengthOptions,
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="bodyFitTop"
            label="Body Fit (Top)"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('body_fit_top', false) ||
              bodyFitTopOptions.length <= 0
            }
            required={isFieldRequired('body_fit_top', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: bodyFitTopOptions,
            }}
          />
          <ControlledFormInput
            name="bodyFitBottom"
            label="Body Fit (Bottom)"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('body_fit_bottom', false) ||
              bodyFitBottomOptions.length <= 0
            }
            required={isFieldRequired('body_fit_bottom', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: bodyFitBottomOptions,
            }}
          />
          <ControlledFormInput
            name="bottomLength"
            label="Bottom Length"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('bottom_length', false) ||
              bottomLengthOptions.length <= 0
            }
            required={isFieldRequired('bottom_length', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: bottomLengthOptions,
            }}
          />
        </Stack>
      </Stack>
    </Section>
  );
}
